import { RATINGS_CONFIG } from "../../PagesUser/Campaign/DataVisualization/Charts/PieChart/PieChart.utils";
import { formatDate } from "../../PagesUser/Campaign/DataVisualization/utils";
import { COLORS, CONFIG, STATUS_NAMES } from "./MetricsReport.constants";
import map from "lodash/map";

export function getRandomValueFrom(array) {
  return array[Math.floor(Math.random() * array.length)];
}

export function createPlaceholderProfilePicture({ name, last }) {
  if (!name) {
    return "";
  }

  const nameLetter = Array.from(name)[0];
  const background = getRandomValueFrom(COLORS) || CONFIG.background;
  const letters = last ? `${nameLetter}+${Array.from(last)[0]}` : nameLetter;

  return `${CONFIG.baseURL}?font-size=${CONFIG.fontSize}&name=${letters}&background=${background}`;
}

export function mapMetricsKeysToObject({ key, metricsData, platforms }) {
  return {
    [key]: [
      { label: "Klicks", value: metricsData[key].totalClicks },
      { label: "Impressionen", value: metricsData[key].totalImpressions },
      {
        label: "CTR",
        value: `${(metricsData[key].totalClicks / metricsData[key].totalImpressions) * 100} %`
      },
      { label: "Bewerber", value: platforms[key] }
    ]
  };
}

export function enrichMetricsData({ metrics }) {
  const {
    campaignData,
    pricePerApplicant,
    applicantsCount,
    ratingsCount,
    daysPerApplicant,
    statuses,
    platforms,
    channelCampaigns,
    showCreativeTable,
    averageRating
  } = metrics;

  // Campaign metrics section
  const metricsData = campaignData?.metrics;

  const {
    startDate: plannedDate,
    campaignLiveDate,
    createdDate,
    endDate,
    locations
  } = campaignData;
  const startDate = new Date(plannedDate || campaignLiveDate || createdDate);

  const start = parseDate(startDate);
  const end = parseDate(new Date(endDate));
  const laufzeit = "Laufzeit " + start + " - " + end;

  let locs = map(locations, (item) => {
    return item.location + " (" + item.country + ")";
  });

  locs = locs.join(", ");

  let channelMetrics = {};

  Object.keys(metricsData).forEach((key) => {
    let ctr = 0;
    let clicks = metricsData[key].totalClicks;

    // Special case for tiktok
    if (key === "tiktok") {
      clicks = metricsData[key].allClicks;
    }

    if (clicks !== 0 && metricsData[key].totalImpressions !== 0) {
      ctr = `${((clicks / metricsData[key].totalImpressions) * 100).toFixed(2)} %`;
    }

    channelMetrics[key] = [
      { label: "Klicks", value: clicks },
      { label: "Impressionen", value: metricsData[key].totalImpressions },
      {
        label: "CTR",
        value: ctr
      },
      { label: "Bewerber", value: platforms[key] || 0 }
    ];
  });

  let numerator, denominator;
  if (applicantsCount && daysPerApplicant && applicantsCount >= 5 && daysPerApplicant < 10) {
    const numeratorArr = [1, 2, 3, 5, 10, 15];
    if (applicantsCount > 0) {
      for (let i = 0; i < numeratorArr.length; i++) {
        numerator = numeratorArr[i];
        if (numerator / daysPerApplicant > 1) {
          denominator = numerator / daysPerApplicant;
          if (parseFloat(Math.abs(Math.round(denominator) - denominator)).toFixed(2) <= 0.35) {
            denominator = Math.round(denominator);
            if (denominator === numerator) {
              denominator = 1;
              numerator = 1;
            }
            break;
          }
        }
      }
    }
  }

  let bewerberMetrics = {};
  let daysText, applicantsText;
  if (numerator && denominator) {
    daysText = numerator > 1 ? `Alle ${numerator} Tage` : "Jeder Tag";
    applicantsText = `${denominator} Bewerber`;

    bewerberMetrics = {
      ["Bewerber-Metriken"]: [
        { label: "Anzahl der Bewerber", value: applicantsCount || 0 },
        { label: "Preis pro Bewerber", value: `${pricePerApplicant || 0} €` },
        { label: daysText, value: applicantsText }
      ]
    };
  } else {
    // Bewerber section
    bewerberMetrics = {
      ["Bewerber-Metriken"]: [
        { label: "Anzahl der Bewerber", value: applicantsCount || 0 },
        { label: "Preis pro Bewerber", value: `${pricePerApplicant || 0} €` }
      ]
    };
  }

  const platformBreakdown = getPlatformBreakdown({
    platforms,
    channels: Object.keys(channelMetrics)
  });
  const statusBreakdown = getStatusesBreakdown({ statuses });
  const ratingsBreakdown = getRatingsBreakdown({ ratings: ratingsCount });

  return {
    platformBreakdown,
    statusBreakdown,
    bewerberMetrics,
    channelMetrics,
    campaignData,
    channelCampaigns,
    laufzeit,
    locations: locs,
    showCreativeTable,
    ratingsBreakdown,
    averageRating
  };
}

export function filterMetricsByDateRange(metrics, range) {
  const { from, to } = range || {};

  if (!from && !to) return metrics;

  const fromDate = from ? formatDate(from) : null;
  const toDate = to ? formatDate(to) : null;

  return metrics.filter((candidate) => {
    const appliedDate = formatDate(candidate.appliedDate);
    return appliedDate >= fromDate && appliedDate <= toDate;
  });
}

function countCandidatesFunnel(candidateLogs, applicantsCount) {
  const candidateStatusLogs = candidateLogs
    .filter((log) => log.type === "CANDIDATE_STATUS")
    .filter((log) => log.value !== STATUS_NAMES.APPLIED);

  // For each status, count the number of unique candidates
  const statusCounter = {};
  statusCounter[STATUS_NAMES.APPLIED] = applicantsCount;

  const groupedLogs = Object.groupBy(candidateStatusLogs, ({ value }) => value);

  // Separate auto-rejected and manually rejected candidates
  const rejectedLogs = groupedLogs[STATUS_NAMES.REJECTED] || [];
  const manualRejectedLogs = rejectedLogs.filter((log) => log.userId !== null);
  const autorejectedLogs = rejectedLogs.filter((log) => log.userId === null);

  groupedLogs[STATUS_NAMES.REJECTED] = manualRejectedLogs;
  groupedLogs[STATUS_NAMES.AUTOMATIC_REJECTED] = autorejectedLogs;

  Object.keys(groupedLogs).forEach((status) => {
    const logsByStatus = groupedLogs[status];
    const candidateIds = logsByStatus.map((log) => log.candidateId);
    const uniqueCandidateIds = new Set(candidateIds);

    if (uniqueCandidateIds.size > 0) {
      statusCounter[status] = uniqueCandidateIds.size;
    }
  });

  return statusCounter;
}

export function enrichDailyMetricsData({ metrics, range }) {
  const filteredMetrics = filterMetricsByDateRange(metrics, range);

  let platforms = {};
  let ratingsMap = {};
  let ratingSum = 0;
  let ratingCount = 0;

  RATINGS_CONFIG.forEach(({ label }) => {
    ratingsMap[label] = 0;
  });

  const candidateLogs = [];

  filteredMetrics.forEach((candidate) => {
    // Collect logs
    if (Array.isArray(candidate.logs)) {
      candidate.logs.forEach((log) => {
        candidateLogs.push({ ...log, candidateId: candidate.id });
      });
    }

    // Platform
    const platform = candidate.platform?.toLowerCase();
    if (platform) {
      platforms[platform] = (platforms[platform] || 0) + 1;
    }

    // Ratings
    if (candidate.ratings?.length === 0) {
      const noInfoLabel = RATINGS_CONFIG.find((rc) => rc.id === 0)?.label;
      if (noInfoLabel) {
        ratingsMap[noInfoLabel] += 1;
      }
    }

    candidate.ratings?.forEach((r) => {
      const value = Number(r.ratingValue);
      const ratingConfig = RATINGS_CONFIG.find((rc) => rc.id === value);

      if (ratingConfig) {
        ratingSum += value;
        ratingCount += 1;
        ratingsMap[ratingConfig.label] += 1;
      }
    });
  });

  // Status breakdown
  const statusCounts = countCandidatesFunnel(candidateLogs, filteredMetrics.length);

  const averageRating = ratingCount > 0 ? parseFloat((ratingSum / ratingCount).toFixed(2)) : 0;

  const platformBreakdown = getPlatformBreakdown({ platforms });
  const statusBreakdown = getStatusesBreakdown({ statuses: statusCounts });
  const ratingsBreakdown = getDailyRatingsBreakdown({ ratings: ratingsMap });

  return {
    statusBreakdown,
    platformBreakdown,
    ratingsBreakdown,
    averageRating
  };
}

export function getPlatformBreakdown({ platforms, channels }) {
  if (!platforms) {
    return {};
  }

  let platformBreakdown = {
    ["Aufschlüsselung der Bewerbungen nach Plattform"]: []
  };

  Object.keys(platforms).forEach((key) => {
    platformBreakdown["Aufschlüsselung der Bewerbungen nach Plattform"].push({
      label: key,
      value: platforms[key]
    });
  });

  if (channels) {
    const platformKeys = Object.keys(platforms);

    channels.forEach((channel) => {
      if (!platformKeys.includes(channel)) {
        platformBreakdown["Aufschlüsselung der Bewerbungen nach Plattform"].push({
          label: channel,
          value: 0
        });
      }
    });
  }

  return platformBreakdown;
}

export function getStatusesBreakdown({ statuses }) {
  if (!statuses) {
    return {};
  }
  let statusBreakdown = {
    ["Aufschlüsselung der Bewerbungen nach Status"]: []
  };

  Object.keys(statuses).forEach((key) => {
    statusBreakdown["Aufschlüsselung der Bewerbungen nach Status"].push({
      label: key,
      value: statuses[key]
    });
  });

  return statusBreakdown;
}

export function getRatingsBreakdown({ ratings }) {
  if (!ratings) {
    return [];
  }

  if (ratings.every((rating) => rating.value === 0)) {
    return [
      {
        label: "No info",
        value: 1
      }
    ];
  }

  return ratings;
}

export function getDailyRatingsBreakdown({ ratings }) {
  const ratingsArray = Object.entries(ratings).map(([label, value]) => ({
    label,
    value
  }));

  if (!ratingsArray) {
    return [];
  }

  if (ratingsArray.every((rating) => rating.value === 0)) {
    return [
      {
        label: "No info",
        value: ratingsArray.length
      }
    ];
  }

  return ratingsArray;
}

export function parseDate(x) {
  if (x) {
    return x.getDate() + "." + (x.getMonth() + 1) + "." + x.getFullYear();
  } else {
    return "DD-MM-YYYY";
  }
}
